import type { Node } from "interfaces/schemas";

type ErrorResult = {
  error: { message: string };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DistributiveOmit<T, K extends PropertyKey> = T extends any
  ? Omit<T, K>
  : never;

type NodeRequestItem = DistributiveOmit<Node, "nodes" | "partiallyVisible">;

type CreateNodesResult =
  | {
      nodesToRequest: NodeRequestItem[];
      subTree: Node;
    }
  | ErrorResult;

interface CreateNodesInput {
  data: Node;
  callback?: (node: Node) => Node;
}

export const createNodesInput = ({
  data,
  callback = (node) => node,
}: CreateNodesInput): CreateNodesResult => {
  const nodesToRequest: NodeRequestItem[] = [];

  const cloneNode = (node: Node, parentId: string | null): Node => {
    const newNode: Node = {
      ...node,
      parentId,
      id: crypto.randomUUID(),
    };

    const processedNode: Node = callback(newNode);

    const {
      nodes: _nodes,
      partiallyVisible: _partiallyVisible,
      ...nodeRequestExtracted
    } = processedNode;
    const nodeRequest: NodeRequestItem = nodeRequestExtracted;
    nodesToRequest.push(nodeRequest);

    if (node.nodes) {
      processedNode.nodes = [
        ...node.nodes.map((child: Node) => cloneNode(child, processedNode.id)),
      ];
    }

    return processedNode;
  };

  try {
    const subTree = cloneNode(data, data.parentId);
    return {
      nodesToRequest,
      subTree,
    };
  } catch (error) {
    console.error("Error creating nodes:", error);
    return {
      error: {
        message:
          error instanceof Error
            ? error.message
            : "An unknown error occurred while creating nodes",
      },
    };
  }
};
